import { createTheme as createMuiTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import { createBreakpoints } from '@mui/system';
import { defaultLightSemanticPalette } from './colors/defaultLight';
import { mergeSemanticPaletteColors } from './utils/mergeSemanticPalette';
import { resolveDashboardPaletteColors } from './resolveDashboardPalette';
import { buildFontFamilyStack } from './typography/baseFonts';
import { MUI_BUTTON_COMPONENTS } from './components/muiButton';
import { SHARED_TYPOGRAPHY_VARIANT_OPTIONS } from './typography/sharedVariantOptions';
import { SPACING_TOKENS } from './spacing/spacingTokens';
import { RADIUS_TOKENS } from './shape/radiusTokens';
import { ELEVATION_TOKENS } from './elevation/elevationTokens';
import { BACKDROP_TOKENS } from './backdrop/backdropTokens';
import { MUI_RADIUS_COMPONENTS } from './components/muiRadius';
import { MUI_ELEVATION_COMPONENTS } from './components/muiElevation';
import { MUI_BACKDROP_COMPONENTS } from './components/muiBackdrop';
import type { AppThemeDefinition, AppThemeRegistry, CreateAppThemeParams } from './types';
import {
  DEFAULT_THEME_VARIANT,
  DEFAULT_WHITELABEL_ID,
  resolveWhitelabelSemanticPartials,
  resolveWhitelabelFontFamily,
} from './whitelabels/registry';

export const DEFAULT_APP_THEME_ID = 'default';

export const APP_THEME_REGISTRY: AppThemeRegistry = {
  default: {
    semantic: defaultLightSemanticPalette,
    muiPaletteMode: 'light',
    backgroundDefault: '#F8F9FB',
  },
};

export function listAppThemeIds(): string[] {
  return Object.keys(APP_THEME_REGISTRY);
}

export function getAppThemeDefinition(themeId: string | undefined): { id: string; definition: AppThemeDefinition } {
  const id = themeId ?? DEFAULT_APP_THEME_ID;
  const definition = APP_THEME_REGISTRY[id];
  if (!definition) {
    throw new Error(`createAppTheme: unknown theme "${id}". ` + `Defined themes: ${listAppThemeIds().join(', ')}.`);
  }
  return { id, definition };
}

const FALLBACK_PAGE_BACKGROUND = '#F8F9FB';

/** Same values as legacy `createTheme` baseline — used by `DefaultDataGrid` (`theme.palette.dataGrid`). */
const LEGACY_PALETTE_DATAGRID = {
  cellBorderRight: '#F0F0F0',
  cellBorderBottom: '#EFEFEF',
  headerBorderRight: '#D9D9D9',
  rowHover: '#F9F9F9',
} as const;

export function createAppTheme(params: CreateAppThemeParams = {}): Theme {
  const themeId = params.theme ?? DEFAULT_APP_THEME_ID;
  const whitelabelId = params.whitelabelId ?? DEFAULT_WHITELABEL_ID;
  const variant = params.variant ?? DEFAULT_THEME_VARIANT;
  const extra: ThemeOptions[] = params.themeOptions ?? [];

  const { definition } = getAppThemeDefinition(themeId);
  const semantic = mergeSemanticPaletteColors(
    definition.semantic,
    ...resolveWhitelabelSemanticPartials(whitelabelId, variant),
  );

  const legacyDashboard = resolveDashboardPaletteColors(whitelabelId);

  const legacyBreakpoints = createBreakpoints({
    values: {
      xs: 0,
      sm: 767,
      md: 1044,
      lg: 1280,
      xl: 1920,
    },
  });
  const legacyTypography = {
    ...SHARED_TYPOGRAPHY_VARIANT_OPTIONS,
    fontFamily: buildFontFamilyStack(resolveWhitelabelFontFamily(whitelabelId, variant)),
  };

  return createMuiTheme(
    {
      space: SPACING_TOKENS,
      radius: RADIUS_TOKENS,
      elevation: ELEVATION_TOKENS,
      backdrop: BACKDROP_TOKENS,
      shape: {
        borderRadius: 12,
      },
      palette: {
        contrastThreshold: 4.5,
        mode: definition.muiPaletteMode,
        background: {
          default: definition.backgroundDefault ?? FALLBACK_PAGE_BACKGROUND,
        },
        primary: semantic.primary,
        error: semantic.error,
        warning: semantic.warning,
        info: semantic.info,
        success: semantic.success,
        dataGrid: LEGACY_PALETTE_DATAGRID,
        dashboard: legacyDashboard,
      },
      breakpoints: legacyBreakpoints,
      typography: legacyTypography,
      components: {
        ...MUI_BUTTON_COMPONENTS,
        ...MUI_RADIUS_COMPONENTS,
        ...MUI_ELEVATION_COMPONENTS,
        ...MUI_BACKDROP_COMPONENTS,
      },
    },
    ...extra,
  );
}

export const defaultAppTheme = createAppTheme();
